import type { FilterFieldDefinition } from '../types/reportTypes';

// ─── Placeholder option-fetching functions ───
// These will be replaced with real implementations in the query layer task.

const fetchAssetOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchCategoryOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchLocationOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchDivisionOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchRegionOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchVendorOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchExpenseCategoryOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchServiceLevelOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

const fetchUserOptions = async (_companyId: string) =>
    [] as { label: string; value: string }[];

/** Complete filter field catalog organized by category */
export const REPORT_FILTER_FIELDS: FilterFieldDefinition[] = [
    // ─── Asset (always available — base filters) ───
    {
        key: 'asset',
        label: 'Asset',
        category: 'asset',
        fieldType: 'dropdown',
        valueInputType: 'searchable-multi-select',
        isBaseFilter: true,
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchAssetOptions,
    },
    {
        key: 'asset-category',
        label: 'Asset Category',
        category: 'asset',
        fieldType: 'dropdown',
        valueInputType: 'searchable-dropdown',
        isBaseFilter: true,
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchCategoryOptions,
    },
    {
        key: 'asset-status',
        label: 'Asset Status (Active / Archived)',
        category: 'asset',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        isBaseFilter: true,
        operators: ['Equals', 'Does Not Equal'],
        optionsFn: async () => [
            { label: 'Active', value: 'active' },
            { label: 'Archived', value: 'archived' },
        ],
    },

    // ─── Organization (always available — base filters) ───
    {
        key: 'location',
        label: 'Location',
        category: 'organization',
        fieldType: 'dropdown',
        valueInputType: 'searchable-dropdown',
        isBaseFilter: true,
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchLocationOptions,
    },
    {
        key: 'division',
        label: 'Division',
        category: 'organization',
        fieldType: 'dropdown',
        valueInputType: 'searchable-dropdown',
        isBaseFilter: true,
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchDivisionOptions,
    },
    {
        key: 'region',
        label: 'Region',
        category: 'organization',
        fieldType: 'dropdown',
        valueInputType: 'searchable-dropdown',
        isBaseFilter: true,
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchRegionOptions,
    },
    {
        key: 'company',
        label: 'Company',
        category: 'organization',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        isBaseFilter: true,
        operators: ['Equals'],
    },

    // ─── Financial (contextual — shown when financial/cost metrics selected) ───
    {
        key: 'vendor',
        label: 'Vendor',
        category: 'financial',
        fieldType: 'dropdown',
        valueInputType: 'searchable-dropdown',
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchVendorOptions,
    },
    {
        key: 'expense-category',
        label: 'Expense Category',
        category: 'financial',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchExpenseCategoryOptions,
    },
    {
        key: 'expense-amount',
        label: 'Expense Amount',
        category: 'financial',
        fieldType: 'number',
        valueInputType: 'numeric-input',
        operators: ['Equals', 'Greater Than', 'Less Than', 'Between', '≥', '≤'],
    },
    {
        key: 'fuel-type',
        label: 'Fuel Type',
        category: 'financial',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
    },
    {
        key: 'labor-type',
        label: 'Labor Type (ST / OT)',
        category: 'financial',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal'],
    },

    // ─── PM (contextual — shown when PM metrics selected) ───
    {
        key: 'service-level',
        label: 'Service Level',
        category: 'pm',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchServiceLevelOptions,
    },
    {
        key: 'pm-status',
        label: 'PM Status (Completed / Overdue / Upcoming)',
        category: 'pm',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In'],
    },

    // ─── Tire (contextual — shown when tire metrics selected) ───
    {
        key: 'tire-status',
        label: 'Tire Status (New / Used / Recap / Repaired)',
        category: 'tire',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
    },
    {
        key: 'tire-failure-reason',
        label: 'Failure Reason',
        category: 'tire',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In'],
    },
    {
        key: 'tire-manufacturer',
        label: 'Manufacturer',
        category: 'tire',
        fieldType: 'dropdown',
        valueInputType: 'searchable-dropdown',
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In', 'Contains'],
    },
    {
        key: 'tire-size',
        label: 'Size',
        category: 'tire',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In'],
    },

    // ─── Safety (contextual — shown when safety metrics selected) ───
    {
        key: 'incident-type',
        label: 'Incident Type',
        category: 'safety',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In'],
    },
    {
        key: 'severity',
        label: 'Severity',
        category: 'safety',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In'],
    },
    {
        key: 'training-type',
        label: 'Training Type',
        category: 'safety',
        fieldType: 'dropdown',
        valueInputType: 'dropdown',
        operators: ['Equals', 'Does Not Equal', 'In'],
    },

    // ─── System (always available — base filters) ───
    {
        key: 'date-of-service',
        label: 'Date of Service',
        category: 'system',
        fieldType: 'date',
        valueInputType: 'date-picker',
        isBaseFilter: true,
        operators: ['On', 'Before', 'After', 'Between', 'In Last X Days'],
    },
    {
        key: 'date-of-input',
        label: 'Date of Input',
        category: 'system',
        fieldType: 'date',
        valueInputType: 'date-picker',
        isBaseFilter: true,
        operators: ['On', 'Before', 'After', 'Between', 'In Last X Days'],
    },
    {
        key: 'created-by',
        label: 'Created By',
        category: 'system',
        fieldType: 'dropdown',
        valueInputType: 'searchable-dropdown',
        isBaseFilter: true,
        operators: ['Equals', 'Does Not Equal', 'In', 'Not In'],
        optionsFn: fetchUserOptions,
    },
    {
        key: 'has-attachment',
        label: 'Has Attachment',
        category: 'system',
        fieldType: 'boolean',
        valueInputType: 'boolean-toggle',
        isBaseFilter: true,
        operators: ['Equals'],
    },
];
